import { z } from 'zod'
import {
  AsyncFn,
  BusinessResourceConflictException,
  BusinessResourceDoesNotExistException,
  BzDateFns,
  Event,
  EventPayload,
  Forceable,
  IEventStore,
  IsoDateString,
  isNullish,
  Log,
  Mutable,
} from '../../common'

import {
  DEFAULT_PRICE_ENGINE_OPTIONS,
  FreeMaintenancePlanActivatedEventData,
  FreeMaintenancePlanActivatedEventName,
  ImportedMaintenancePlanActivatedEventData,
  ImportedMaintenancePlanActivatedEventName,
  MaintenancePlanActivatedEventData,
  MaintenancePlanActivatedEventName,
  MaintenancePlanActivatedWithOneTimePaymentEventData,
  MaintenancePlanActivatedWithOneTimePaymentEventName,
  MaintenancePlanActivatedWithoutPaymentEventData,
  MaintenancePlanActivatedWithoutPaymentEventName,
  MaintenancePlanBusinessUnitSetEventData,
  MaintenancePlanBusinessUnitSetEventName,
  MaintenancePlanCanceledEventData,
  MaintenancePlanCanceledEventName,
  MaintenancePlanCancellationReasonType,
  MaintenancePlanConfiguredEventData,
  MaintenancePlanConfiguredEventName,
  MaintenancePlanCreatedEventData,
  MaintenancePlanCreatedEventName,
  MaintenancePlanExpiredEventName,
  MaintenancePlanFixedDurationSetEventName,
  MaintenancePlanHistoryImportedEventData,
  MaintenancePlanHistoryImportedEventName,
  MaintenancePlanLapsedEventName,
  MaintenancePlanLifecycleCorrectedEventData,
  MaintenancePlanLifecycleCorrectedEventName,
  MaintenancePlanMigratedToStaticPricingEventData,
  MaintenancePlanMigratedToStaticPricingEventName,
  MaintenancePlanPaymentInterval,
  MaintenancePlanPaymentReceivedEventData,
  MaintenancePlanPaymentReceivedEventName,
  MaintenancePlanPricingSetEventData,
  MaintenancePlanPricingSetEventName,
  MaintenancePlanReplenishmentAnchorSetEventData,
  MaintenancePlanReplenishmentAnchorSetEventName,
  MaintenancePlanRescheduledEventData,
  MaintenancePlanRescheduledEventName,
  MaintenancePlanScheduledActivationEventData,
  MaintenancePlanScheduledActivationEventName,
  MaintenancePlanScheduledEventData,
  MaintenancePlanScheduledEventName,
  MaintenancePlanSetSoldByEventData,
  MaintenancePlanSetSoldByEventName,
  NumPeriodsPerYearForInterval,
} from '../../contracts'
import { ForCompany, ForCompanyUser } from '../Company/Company'
import { bzOptional, Guid } from '../common-schemas'
import { TimeWindow } from '../DateTime/TimeWindow'
import { calculateSimplePriceOrderSummaryUsc } from '../Finance/Transactions/TransactionFunctionsUsc'
import { CartOrderSummaryUsc } from '../Finance/Transactions/TransactionTypes'
import { BreezySystemUserGuid, ForUser, UserGuidContainer } from '../Users/User'
import { toVisitReplenishmentAnchorOverrideLocalDate } from './MaintenancePlanConfigTypes'
import { MaintenancePlanMigrationInputData } from './MaintenancePlanMigrationTypes'
import { MaintenancePlanCreationRequest } from './MaintenancePlanRequestTypes'
import {
  BasicMaintenancePlanViewModel,
  BasicTemporalMaintenancePlanViewModel,
  MaintenancePlanEntityTypeName,
  MaintenancePlanGuidContainer,
  MaintenancePlanPaymentFlow,
  MaintenancePlanStatus,
} from './MaintenancePlanTypes'

const notInitializedEx = () =>
  new BusinessResourceDoesNotExistException('Maintenance Plan cannot be found and most likely does not exist')

const assertCanSetVisitReplenishmentAnchor = (
  maintenancePlan: Pick<
    BasicTemporalMaintenancePlanViewModel,
    'maintenancePlanGuid' | 'paymentFlow' | 'status' | 'isFreePlan' | 'yearlyPriceUsc'
  >,
): void => {
  if (maintenancePlan.status !== MaintenancePlanStatus.ACTIVE) {
    throw new BusinessResourceConflictException(
      `Tried to set visit replenishment anchor for maintenance plan ${maintenancePlan.maintenancePlanGuid} but plan is not active.`,
    )
  }

  if (maintenancePlan.paymentFlow !== MaintenancePlanPaymentFlow.AUTO) {
    throw new BusinessResourceConflictException(
      `Tried to set visit replenishment anchor for maintenance plan ${maintenancePlan.maintenancePlanGuid} but plan is not auto-renewing.`,
    )
  }

  if (maintenancePlan.isFreePlan || maintenancePlan.yearlyPriceUsc === 0) {
    throw new BusinessResourceConflictException(
      `Tried to set visit replenishment anchor for maintenance plan ${maintenancePlan.maintenancePlanGuid} but free plans always replenish from activation.`,
    )
  }
}

const emptyCart: CartOrderSummaryUsc = {
  totalPriceUsc: 0,
  taxAmountUsc: 0,
  discountAmountUsc: 0,
  upchargeAmountUsc: 0,
  subtotalPriceUsc: 0,
  creditAmountUsc: 0,
}

const withoutUserGuid = <TEventData>(e: UserGuidContainer & TEventData): Omit<TEventData, 'userGuid'> => {
  const { userGuid, ...eventData } = e
  return eventData
}

export class MaintenancePlan {
  private eventHistory: Event<unknown>[] = []
  private internalModel: Mutable<BasicMaintenancePlanViewModel> | undefined
  // NOTE: Injectable for Testing
  private readonly clock: () => IsoDateString

  static load = async (
    eventStore: IEventStore,
    req: ForCompany<MaintenancePlanGuidContainer>,
  ): Promise<MaintenancePlan> => new MaintenancePlan(eventStore).load(req)

  static readViewModel = async (
    eventStore: IEventStore,
    req: ForCompany<MaintenancePlanGuidContainer>,
  ): Promise<BasicTemporalMaintenancePlanViewModel> => {
    return await MaintenancePlan.load(eventStore, req).then(m => m.getComprehensiveViewModel())
  }

  static assertCanSetVisitReplenishmentAnchor(
    maintenancePlan: Pick<
      BasicTemporalMaintenancePlanViewModel,
      'maintenancePlanGuid' | 'paymentFlow' | 'status' | 'isFreePlan' | 'yearlyPriceUsc'
    >,
  ): void {
    assertCanSetVisitReplenishmentAnchor(maintenancePlan)
  }

  static invoke = async (
    eventStore: IEventStore,
    req: ForCompany<MaintenancePlanGuidContainer>,
    f: (mp: MaintenancePlan) => Promise<void>,
  ): Promise<void> => {
    return MaintenancePlan.load(eventStore, req).then(f)
  }

  constructor(
    private readonly eventStore: IEventStore,
    clockOverride?: () => IsoDateString,
  ) {
    this.clock = clockOverride ? clockOverride : () => new Date().toISOString()
  }

  // Queries

  isInitialized = (): boolean => !!this.internalModel

  isFreePlan = (): boolean => !!this.internalModel?.isFreePlan

  getComprehensiveViewModel = (): BasicTemporalMaintenancePlanViewModel => {
    if (!this.internalModel) throw notInitializedEx()

    return { ...this.internalModel }
  }

  // Commands

  //prettier-ignore
  init = async (req: ForCompanyUser<MaintenancePlanCreationRequest>): Promise<MaintenancePlan> => {
    if (this.isInitialized()) {
      throw new BusinessResourceConflictException('Maintenance Plan is already initialized')
    }

    this.internalModel = {
      ...req,
      ...emptyCart,
      maintenancePlanGuid: req.maintenancePlanGuid,
      maintenancePlanVersion: 0,
      status: MaintenancePlanStatus.NONE,
      isPricingInitialized: false,
      totalPaymentsReceivedUsc: 0,
      totalPaymentsEverReceivedUsc: 0,
      yearlyPriceUsc: 0,
      isFreePlan: false,
      paymentFlow: MaintenancePlanPaymentFlow.NONE,
      createdByUserGuid: req.userGuid,
      payments: {},
      numConfigurationsEverSet: 0,
      isMigratedToStaticPricing: false,
      priceEngineOptions: req.pricing?.priceEngineOptions ?? DEFAULT_PRICE_ENGINE_OPTIONS,
    }

    await this.persistAndApplyEvent(
      this.createNextEvent<MaintenancePlanCreatedEventData>({
        eventType: MaintenancePlanCreatedEventName,
        eventData: {
          accountGuid: req.accountGuid,
          locationGuid: req.locationGuid,
          businessUnitGuid: req.businessUnitGuid,
          taxRate: req.taxRate,
          soldByUserGuid: req.soldByUserGuid,
        },
        userGuid: req.userGuid,
      }),
    )

    if (req.configuration) {
      await this.setConfiguration({ ...req.configuration, userGuid: req.userGuid })
    }

    if (req.pricing) {
      await this.setPricing({ ...req.pricing, userGuid: req.userGuid })
    }

    return this
  }

  initMigrated = async (req: ForCompanyUser<MaintenancePlanMigrationInputData>): Promise<MaintenancePlan> => {
    const creationConfiguration: MaintenancePlanConfiguredEventData = {
      maintenancePlanDefinitionGuid: req.configuration.maintenancePlanDefinitionGuid,
      numVisitsPerYear: req.configuration.numVisitsPerYear,
      numDaysUntilVisitCreditExpiration: req.numDaysUntilAutoCancelation,
      coveredEquipment: [],
      discounts: req.configuration.discounts,

      suppressActivationEmail: true,
      initialVisits: [],

      yearlyStaticPriceUsc: req.configuration.yearlyStaticPriceUsc,
    }

    const creationRequest: ForCompanyUser<MaintenancePlanCreationRequest> = {
      companyGuid: req.companyGuid,
      userGuid: req.userGuid,

      maintenancePlanGuid: req.maintenancePlanGuid,
      accountGuid: req.accountGuid,
      locationGuid: req.locationGuid,
      taxRate: req.taxRate,
      configuration: creationConfiguration,
      pricing: req.pricing,
    }
    await this.init(creationRequest)

    await this.pushForUser<MaintenancePlanHistoryImportedEventData>(MaintenancePlanHistoryImportedEventName, {
      userGuid: req.userGuid,
      activatedAt: req.activatedAt,
      numDaysUntilAutoCancelation: req.numDaysUntilAutoCancelation,
      historicalPaymentsReceived: req.historicalPaymentsReceived,
    })

    return this
  }

  load = async (req: ForCompany<MaintenancePlanGuidContainer>): Promise<MaintenancePlan> => {
    this.requireNotInitialized()
    const events = await this.eventStore.read({ entityGuid: req.maintenancePlanGuid, companyGuid: req.companyGuid })
    this.eventHistory = events
    events.map(this.applyEvent)
    return this
  }

  atInstant = (at: IsoDateString): MaintenancePlan => {
    this.internalModel = undefined
    this.eventHistory.filter(e => e.occurredAt <= at).map(e => this.applyEvent(e))
    return this
  }

  setConfiguration = async (req: ForUser<MaintenancePlanConfiguredEventData>): Promise<void> =>
    this.pushForUser(MaintenancePlanConfiguredEventName, req)

  setPricing = async (req: ForUser<MaintenancePlanPricingSetEventData>): Promise<void> =>
    this.pushForUser(MaintenancePlanPricingSetEventName, req)

  setAsFixedDuration = async (req: UserGuidContainer): Promise<void> =>
    this.pushForUser(MaintenancePlanFixedDurationSetEventName, req)

  setBusinessUnit = async (req: ForUser<MaintenancePlanBusinessUnitSetEventData>): Promise<void> =>
    this.pushForUser(MaintenancePlanBusinessUnitSetEventName, req)

  setVisitReplenishmentAnchor = async (req: ForUser<MaintenancePlanReplenishmentAnchorSetEventData>): Promise<void> => {
    this.requireInitialized()
    MaintenancePlan.assertCanSetVisitReplenishmentAnchor(this.getComprehensiveViewModel())

    return this.pushForUser(MaintenancePlanReplenishmentAnchorSetEventName, req)
  }

  activate = async (req: ForUser<MaintenancePlanActivatedEventData>): Promise<void> =>
    this.pushForUser(MaintenancePlanActivatedEventName, req)

  activateFreePlan = async (req: ForUser<FreeMaintenancePlanActivatedEventData>): Promise<void> => {
    this.requireInitialized()
    if (!this.internalModel?.isFreePlan) {
      throw new BusinessResourceConflictException(
        `Tried to activate free maintenance plan ${this.internalModel?.maintenancePlanGuid} but plan is not free.`,
      )
    }

    this.pushForUser(FreeMaintenancePlanActivatedEventName, req)
  }

  activateImported = async (req: ForUser<ImportedMaintenancePlanActivatedEventData>): Promise<void> => {
    this.requireInitialized()
    this.pushForUser(ImportedMaintenancePlanActivatedEventName, {
      userGuid: req.userGuid,
      activatedAt: req.activatedAt,
      numDaysUntilAutoCancelation: req.numDaysUntilAutoCancelation,
    })
  }

  activateWithOneTimePayment = async (
    req: ForUser<MaintenancePlanActivatedWithOneTimePaymentEventData>,
  ): Promise<void> => this.pushForUser(MaintenancePlanActivatedWithOneTimePaymentEventName, req)

  // Activates a MANUAL plan whose linked invoice was paid in full with no payment record (e.g. a $0 /
  // fully-discounted invoice). Records no payment. Callers MUST guard on status === PENDING (see
  // WithMaintenancePlanActivationOnInvoicePaid); this method does not re-check status itself, mirroring
  // activateWithOneTimePayment.
  activateWithoutPayment = async (req: ForUser<MaintenancePlanActivatedWithoutPaymentEventData>): Promise<void> =>
    this.pushForUser(MaintenancePlanActivatedWithoutPaymentEventName, req)

  schedule = async (req: ForUser<MaintenancePlanScheduledEventData>): Promise<void> => {
    this.requireInitialized()
    if (this.internalModel?.status !== MaintenancePlanStatus.PENDING) {
      throw new BusinessResourceConflictException(
        `Cannot schedule plan ${this.internalModel?.maintenancePlanGuid} — status must be PENDING, got ${this.internalModel?.status}`,
      )
    }

    return this.pushForUser(MaintenancePlanScheduledEventName, req)
  }

  activateScheduled = async (req: ForUser<MaintenancePlanScheduledActivationEventData>): Promise<void> => {
    this.requireInitialized()
    if (this.internalModel?.status !== MaintenancePlanStatus.SCHEDULED) {
      throw new BusinessResourceConflictException(
        `Cannot activate scheduled plan ${this.internalModel?.maintenancePlanGuid} — status is ${this.internalModel?.status}`,
      )
    }

    return this.pushForUser(MaintenancePlanScheduledActivationEventName, req)
  }

  reschedule = async (req: ForUser<MaintenancePlanRescheduledEventData>): Promise<void> => {
    this.requireInitialized()
    const isScheduled = this.internalModel?.status === MaintenancePlanStatus.SCHEDULED
    const isCollectLaterPendingWithDate =
      this.internalModel?.status === MaintenancePlanStatus.PENDING && !!this.internalModel?.scheduledActivationAt

    if (!isScheduled && !isCollectLaterPendingWithDate) {
      throw new BusinessResourceConflictException(
        'Can only reschedule SCHEDULED plans or Collect Later PENDING plans with a scheduled activation date',
      )
    }

    return this.pushForUser(MaintenancePlanRescheduledEventName, req)
  }

  markCanceled = async (req: Forceable<ForUser<MaintenancePlanCanceledEventData>>): Promise<void> => {
    if (!req.force && this.internalModel?.status === MaintenancePlanStatus.CANCELED) return

    return this.pushForUser(MaintenancePlanCanceledEventName, req)
  }

  markLapsed = async (req: UserGuidContainer): Promise<void> => {
    if (this.internalModel?.status === MaintenancePlanStatus.LAPSED) return

    return this.pushForUser(MaintenancePlanLapsedEventName, req)
  }

  markExpired = async (req: UserGuidContainer): Promise<void> => {
    if (this.internalModel?.status === MaintenancePlanStatus.EXPIRED) return
    if (this.internalModel?.status !== MaintenancePlanStatus.ACTIVE) {
      throw new BusinessResourceConflictException(
        `Tried to mark maintenance plan ${this.internalModel?.maintenancePlanGuid} as expired but plan is not active`,
      )
    }

    return this.pushForUser(MaintenancePlanExpiredEventName, req)
  }

  correctLifecycle = async (req: ForUser<MaintenancePlanLifecycleCorrectedEventData>): Promise<void> => {
    this.requireInitialized()
    const vm = this.getComprehensiveViewModel()
    const persistedEvents = await this.eventStore.read({
      companyGuid: vm.companyGuid,
      entityGuid: vm.maintenancePlanGuid,
    })
    const correctionEventsForExecution = persistedEvents.filter(
      event =>
        event.eventType === MaintenancePlanLifecycleCorrectedEventName &&
        (event.eventData as MaintenancePlanLifecycleCorrectedEventData).executionId === req.executionId,
    )
    if (
      correctionEventsForExecution.some(
        event => (event.eventData as MaintenancePlanLifecycleCorrectedEventData).manifestSha256 === req.manifestSha256,
      )
    ) {
      return
    }
    if (correctionEventsForExecution.length > 0) {
      throw new BusinessResourceConflictException(
        `Lifecycle correction execution ${req.executionId} already used a different manifest`,
      )
    }

    if (vm.status !== MaintenancePlanStatus.ACTIVE && vm.status !== MaintenancePlanStatus.EXPIRED) {
      throw new BusinessResourceConflictException(
        `Cannot correct maintenance plan ${vm.maintenancePlanGuid} from status ${vm.status}`,
      )
    }
    let commercialPreState = vm
    if (vm.status === MaintenancePlanStatus.EXPIRED) {
      const terminalEvent = persistedEvents.at(-1)
      if (
        terminalEvent?.eventType !== MaintenancePlanExpiredEventName ||
        terminalEvent.actingUserGuid !== BreezySystemUserGuid
      ) {
        throw new BusinessResourceConflictException(
          `Cannot correct maintenance plan ${vm.maintenancePlanGuid} without a terminal system-authored expiry`,
        )
      }
      const eventsBeforeExpiry = persistedEvents.slice(0, -1)
      commercialPreState = await MaintenancePlan.readViewModel(
        {
          read: async () => eventsBeforeExpiry,
          create: async () => {
            throw new BusinessResourceConflictException('Historical correction pre-state is read-only')
          },
        },
        { companyGuid: vm.companyGuid, maintenancePlanGuid: vm.maintenancePlanGuid },
      )
      if (commercialPreState.status !== MaintenancePlanStatus.ACTIVE) {
        throw new BusinessResourceConflictException(
          `Cannot correct maintenance plan ${vm.maintenancePlanGuid} whose pre-expiry status was ${commercialPreState.status}`,
        )
      }
    }
    if (commercialPreState.paymentFlow !== MaintenancePlanPaymentFlow.MANUAL) {
      throw new BusinessResourceConflictException(
        `Cannot correct maintenance plan ${vm.maintenancePlanGuid} with payment flow ${commercialPreState.paymentFlow}`,
      )
    }
    if (commercialPreState.paymentSubscriptionGuid || commercialPreState.cancellation) {
      throw new BusinessResourceConflictException(
        `Cannot correct maintenance plan ${vm.maintenancePlanGuid} with a subscription or cancellation`,
      )
    }
    if (new Date(req.terminatesAt).getTime() <= new Date(req.activatedAt).getTime()) {
      throw new BusinessResourceConflictException('Corrected maintenance plan term must end after activation')
    }

    const configuredVisitGuids = new Set((vm.configuration?.initialVisits ?? []).map(visit => visit.visitGuid))
    const sourceVisitGuids = new Set<string>()
    const targetVisitGuids = new Set<string>()
    for (const correction of req.visitCorrections) {
      if (!configuredVisitGuids.has(correction.sourceVisitGuid)) {
        throw new BusinessResourceConflictException(
          `Cannot correct unknown configured visit ${correction.sourceVisitGuid} on maintenance plan ${vm.maintenancePlanGuid}`,
        )
      }
      if (
        correction.targetVisitGuid !== correction.sourceVisitGuid &&
        configuredVisitGuids.has(correction.targetVisitGuid)
      ) {
        throw new BusinessResourceConflictException(
          `Cannot replace configured visit ${correction.sourceVisitGuid} with existing visit ${correction.targetVisitGuid}`,
        )
      }
      if (sourceVisitGuids.has(correction.sourceVisitGuid) || targetVisitGuids.has(correction.targetVisitGuid)) {
        throw new BusinessResourceConflictException('Lifecycle correction contains duplicate visit identities')
      }
      sourceVisitGuids.add(correction.sourceVisitGuid)
      targetVisitGuids.add(correction.targetVisitGuid)
    }

    return this.pushForUser(MaintenancePlanLifecycleCorrectedEventName, req)
  }

  recordPayment = async (req: ForUser<MaintenancePlanPaymentReceivedEventData>): Promise<void> => {
    if ((this.internalModel?.payments[req.paymentRecordGuid] ?? 0) > 0) return

    return this.pushForUser(MaintenancePlanPaymentReceivedEventName, req)
  }

  setSoldBy = async (req: ForUser<MaintenancePlanSetSoldByEventData>): Promise<void> =>
    this.pushForUser(MaintenancePlanSetSoldByEventName, req)

  // Internal

  private applyEvent = (e: Event<unknown>): void => {
    if (e.eventType === MaintenancePlanCreatedEventName) {
      const { entityGuid, companyGuid, entityVersion } = e
      const { accountGuid, locationGuid, taxRate, businessUnitGuid, soldByUserGuid } =
        e.eventData as MaintenancePlanCreatedEventData
      this.internalModel = {
        ...emptyCart,
        maintenancePlanGuid: entityGuid,
        maintenancePlanVersion: entityVersion,
        companyGuid,
        accountGuid,
        locationGuid,
        businessUnitGuid,
        status: MaintenancePlanStatus.NONE,
        totalPaymentsReceivedUsc: 0,
        totalPaymentsEverReceivedUsc: 0,
        isPricingInitialized: false,
        yearlyPriceUsc: 0,
        paymentFlow: MaintenancePlanPaymentFlow.NONE,
        isFreePlan: false,
        taxRate,
        createdByUserGuid: e.actingUserGuid,
        payments: {},
        numConfigurationsEverSet: 0,
        isMigratedToStaticPricing: false,
        soldByUserGuid,
        priceEngineOptions: DEFAULT_PRICE_ENGINE_OPTIONS,
      }
    }

    if (!this.internalModel) throw notInitializedEx()

    this.internalModel.maintenancePlanVersion = e.entityVersion

    if (e.eventType === MaintenancePlanConfiguredEventName) {
      const eventData = e.eventData as MaintenancePlanConfiguredEventData
      const hasDeferredPendingStatus =
        this.internalModel.status === MaintenancePlanStatus.PENDING && !!this.internalModel.scheduledActivationAt
      const shouldPreserveScheduledActivation =
        (this.internalModel.status === MaintenancePlanStatus.SCHEDULED || hasDeferredPendingStatus) &&
        !!this.internalModel.scheduledActivationAt

      this.internalModel.configuration = {
        ...eventData,
        scheduledActivationAt: shouldPreserveScheduledActivation
          ? this.internalModel.scheduledActivationAt
          : eventData.scheduledActivationAt,
      }
      this.internalModel.numConfigurationsEverSet++
      const status = this.internalModel.status
      if (
        status !== MaintenancePlanStatus.SCHEDULED &&
        (!this.internalModel.paymentSubscriptionGuid ||
          (status !== MaintenancePlanStatus.ACTIVE && status !== MaintenancePlanStatus.LAPSED))
      )
        this.internalModel.status = MaintenancePlanStatus.PENDING

      if (!shouldPreserveScheduledActivation) {
        this.internalModel.scheduledActivationAt = eventData.scheduledActivationAt
      }
    }

    if (e.eventType === MaintenancePlanBusinessUnitSetEventName) {
      this.internalModel.businessUnitGuid = (e.eventData as MaintenancePlanBusinessUnitSetEventData).businessUnitGuid
    }

    if (e.eventType === MaintenancePlanReplenishmentAnchorSetEventName) {
      const data = e.eventData as MaintenancePlanReplenishmentAnchorSetEventData
      if ('clear' in data && data.clear) {
        this.internalModel.visitReplenishmentAnchorOverride = undefined
      } else if ('replenishmentMonth' in data && 'replenishmentDay' in data) {
        this.internalModel.visitReplenishmentAnchorOverride = toVisitReplenishmentAnchorOverrideLocalDate({
          month: data.replenishmentMonth,
          day: data.replenishmentDay,
        })
      }
    }

    if (e.eventType === MaintenancePlanActivatedEventName) {
      const { customBillingStartAt, numDaysUntilAutoCancelation } = e.eventData as MaintenancePlanActivatedEventData
      const billingStartAt = customBillingStartAt ?? e.occurredAt
      // "Forgets" history of previous plan upon activation of a new plan
      if (this.internalModel.status === MaintenancePlanStatus.CANCELED) {
        this.internalModel.payments = {}
        this.internalModel.totalPaymentsReceivedUsc = 0
      }
      vmSetActivatedAt(this.internalModel, e.occurredAt, numDaysUntilAutoCancelation)

      this.internalModel.paymentSubscriptionGuid = (
        e.eventData as MaintenancePlanActivatedEventData
      ).paymentSubscriptionGuid
      this.internalModel.paymentFlow = MaintenancePlanPaymentFlow.AUTO
      this.internalModel.customBillingStartAt = customBillingStartAt
      this.internalModel.billingStartAt = billingStartAt
      this.internalModel.scheduledActivationAt = undefined
    }

    if (e.eventType === FreeMaintenancePlanActivatedEventName) {
      if (!this.internalModel.isFreePlan) {
        Log.error(`Should not have activated a free plan if the plan was not free`, { vm: this.internalModel })
        return
      }

      const { numDaysUntilAutoCancelation } = e.eventData as FreeMaintenancePlanActivatedEventData

      vmSetActivatedAt(this.internalModel, e.occurredAt, numDaysUntilAutoCancelation)

      this.internalModel.paymentFlow = isNullish(numDaysUntilAutoCancelation)
        ? MaintenancePlanPaymentFlow.AUTO
        : MaintenancePlanPaymentFlow.MANUAL
      this.internalModel.scheduledActivationAt = undefined
    }

    if (e.eventType === ImportedMaintenancePlanActivatedEventName) {
      const { activatedAt, numDaysUntilAutoCancelation } = e.eventData as ImportedMaintenancePlanActivatedEventData
      vmSetActivatedAt(this.internalModel, activatedAt, numDaysUntilAutoCancelation)
      this.internalModel.paymentFlow = MaintenancePlanPaymentFlow.MANUAL
      this.internalModel.scheduledActivationAt = undefined
    }

    if (e.eventType === MaintenancePlanFixedDurationSetEventName) {
      this.internalModel.terminatesAt = vmCalcFixedDurationTerminatesAt(
        this.internalModel.scheduledActivationAt ?? e.occurredAt,
        this.internalModel.configuration?.numFixedDurationYears ?? 1,
      )
      this.internalModel.paymentFlow = MaintenancePlanPaymentFlow.MANUAL
    }

    if (e.eventType === MaintenancePlanActivatedWithOneTimePaymentEventName) {
      const { paymentRecordGuid, paymentAmountUsc, numDaysUntilAutoCancelation, activatedAt } =
        e.eventData as MaintenancePlanActivatedWithOneTimePaymentEventData

      // "Forgets" history of previous plan upon activation of a new plan
      if (this.internalModel.status === MaintenancePlanStatus.CANCELED) {
        this.internalModel.payments = {}
        this.internalModel.totalPaymentsReceivedUsc = 0
      }

      this.internalModel.payments[paymentRecordGuid] = paymentAmountUsc
      this.internalModel.totalPaymentsReceivedUsc += paymentAmountUsc
      this.internalModel.paymentFlow = MaintenancePlanPaymentFlow.MANUAL
      if (this.internalModel.paymentInterval !== MaintenancePlanPaymentInterval.YEARLY) {
        const previousPaymentInterval = this.internalModel.paymentInterval
        this.internalModel.paymentInterval = MaintenancePlanPaymentInterval.YEARLY
        if (previousPaymentInterval === MaintenancePlanPaymentInterval.MONTHLY) {
          this.internalModel.subtotalPriceUsc = this.internalModel.subtotalPriceUsc * 12
          this.internalModel.discountAmountUsc = this.internalModel.discountAmountUsc * 12
          this.internalModel.upchargeAmountUsc = this.internalModel.upchargeAmountUsc * 12
          this.updatePricingSummary()
        }
        if (previousPaymentInterval === MaintenancePlanPaymentInterval.QUARTERLY) {
          this.internalModel.subtotalPriceUsc = this.internalModel.subtotalPriceUsc * 4
          this.internalModel.discountAmountUsc = this.internalModel.discountAmountUsc * 4
          this.internalModel.upchargeAmountUsc = this.internalModel.upchargeAmountUsc * 4
          this.updatePricingSummary()
        }
      }

      vmSetActivatedAt(this.internalModel, activatedAt ?? e.occurredAt, numDaysUntilAutoCancelation)
      this.internalModel.scheduledActivationAt = undefined
    }

    if (e.eventType === MaintenancePlanActivatedWithoutPaymentEventName) {
      const { numDaysUntilAutoCancelation, activatedAt } =
        e.eventData as MaintenancePlanActivatedWithoutPaymentEventData

      // "Forgets" history of previous plan upon activation of a new plan
      if (this.internalModel.status === MaintenancePlanStatus.CANCELED) {
        this.internalModel.payments = {}
        this.internalModel.totalPaymentsReceivedUsc = 0
      }

      // No payment is recorded — activation is funded by a $0 / fully-discounted invoice.
      this.internalModel.paymentFlow = MaintenancePlanPaymentFlow.MANUAL
      // Normalize to a yearly term identically to the one-time-payment activation, so a plan reaching this
      // path on a non-yearly interval annualizes its pricing consistently rather than diverging.
      if (this.internalModel.paymentInterval !== MaintenancePlanPaymentInterval.YEARLY) {
        const previousPaymentInterval = this.internalModel.paymentInterval
        this.internalModel.paymentInterval = MaintenancePlanPaymentInterval.YEARLY
        if (previousPaymentInterval === MaintenancePlanPaymentInterval.MONTHLY) {
          this.internalModel.subtotalPriceUsc = this.internalModel.subtotalPriceUsc * 12
          this.internalModel.discountAmountUsc = this.internalModel.discountAmountUsc * 12
          this.internalModel.upchargeAmountUsc = this.internalModel.upchargeAmountUsc * 12
          this.updatePricingSummary()
        }
        if (previousPaymentInterval === MaintenancePlanPaymentInterval.QUARTERLY) {
          this.internalModel.subtotalPriceUsc = this.internalModel.subtotalPriceUsc * 4
          this.internalModel.discountAmountUsc = this.internalModel.discountAmountUsc * 4
          this.internalModel.upchargeAmountUsc = this.internalModel.upchargeAmountUsc * 4
          this.updatePricingSummary()
        }
      }
      vmSetActivatedAt(this.internalModel, activatedAt ?? e.occurredAt, numDaysUntilAutoCancelation)
      this.internalModel.scheduledActivationAt = undefined
    }

    if (e.eventType === MaintenancePlanScheduledEventName) {
      const { scheduledActivationAt, numDaysUntilAutoCancelation, payment } =
        e.eventData as MaintenancePlanScheduledEventData

      this.internalModel.status = MaintenancePlanStatus.SCHEDULED
      this.internalModel.scheduledActivationAt = scheduledActivationAt
      this.internalModel.numDaysUntilAutoCancelation = numDaysUntilAutoCancelation
      if (this.internalModel.terminatesAt) {
        this.internalModel.terminatesAt = vmCalcFixedDurationTerminatesAt(
          scheduledActivationAt,
          this.internalModel.configuration?.numFixedDurationYears ?? 1,
        )
      }

      if (payment) {
        this.internalModel.payments[payment.paymentRecordGuid] = payment.paymentAmountUsc
        this.internalModel.totalPaymentsReceivedUsc += payment.paymentAmountUsc
        this.internalModel.paymentFlow = MaintenancePlanPaymentFlow.MANUAL
        if (this.internalModel.paymentInterval !== MaintenancePlanPaymentInterval.YEARLY) {
          const previousPaymentInterval = this.internalModel.paymentInterval
          this.internalModel.paymentInterval = MaintenancePlanPaymentInterval.YEARLY
          if (previousPaymentInterval === MaintenancePlanPaymentInterval.MONTHLY) {
            this.internalModel.subtotalPriceUsc = this.internalModel.subtotalPriceUsc * 12
            this.internalModel.discountAmountUsc = this.internalModel.discountAmountUsc * 12
            this.internalModel.upchargeAmountUsc = this.internalModel.upchargeAmountUsc * 12
            this.updatePricingSummary()
          }
          if (previousPaymentInterval === MaintenancePlanPaymentInterval.QUARTERLY) {
            this.internalModel.subtotalPriceUsc = this.internalModel.subtotalPriceUsc * 4
            this.internalModel.discountAmountUsc = this.internalModel.discountAmountUsc * 4
            this.internalModel.upchargeAmountUsc = this.internalModel.upchargeAmountUsc * 4
            this.updatePricingSummary()
          }
        }
      }
    }

    if (e.eventType === MaintenancePlanScheduledActivationEventName) {
      if (this.internalModel.isFreePlan) {
        this.internalModel.paymentFlow = isNullish(this.internalModel.numDaysUntilAutoCancelation)
          ? MaintenancePlanPaymentFlow.AUTO
          : MaintenancePlanPaymentFlow.MANUAL
      }

      vmSetActivatedAt(this.internalModel, e.occurredAt, this.internalModel.numDaysUntilAutoCancelation)
      this.internalModel.scheduledActivationAt = undefined
    }

    if (e.eventType === MaintenancePlanRescheduledEventName) {
      const { scheduledActivationAt, updatedInitialVisits } = e.eventData as MaintenancePlanRescheduledEventData
      this.internalModel.scheduledActivationAt = scheduledActivationAt
      if (this.internalModel.terminatesAt) {
        this.internalModel.terminatesAt = vmCalcFixedDurationTerminatesAt(
          scheduledActivationAt,
          this.internalModel.configuration?.numFixedDurationYears ?? 1,
        )
      }
      if (this.internalModel.configuration) {
        this.internalModel.configuration = {
          ...this.internalModel.configuration,
          scheduledActivationAt,
          initialVisits: updatedInitialVisits ?? this.internalModel.configuration.initialVisits,
        }
      }
    }

    if (e.eventType === MaintenancePlanHistoryImportedEventName) {
      const { activatedAt, numDaysUntilAutoCancelation, historicalPaymentsReceived } =
        e.eventData as MaintenancePlanHistoryImportedEventData
      vmSetActivatedAt(this.internalModel, activatedAt, numDaysUntilAutoCancelation)
      this.internalModel.paymentFlow = MaintenancePlanPaymentFlow.MANUAL
      for (let i = 0; i < historicalPaymentsReceived.length; i++) {
        const payment = historicalPaymentsReceived[i]
        this.internalModel.payments[payment.paymentRecordGuid ?? i] = payment.paymentAmountUsc
        this.internalModel.totalPaymentsReceivedUsc += payment.paymentAmountUsc
      }
    }

    if (e.eventType === MaintenancePlanCanceledEventName) {
      const data = e.eventData as MaintenancePlanCanceledEventData
      this.internalModel.status = MaintenancePlanStatus.CANCELED
      this.internalModel.paymentFlow = MaintenancePlanPaymentFlow.NONE
      this.internalModel.lapsedAt = undefined
      this.internalModel.cancellation = {
        canceledAt: e.occurredAt,
        cancellationReason:
          data.cancellationReason ?? this.internalModel.cancellation?.cancellationReason ?? 'Unspecified',
        cancellationReasonType:
          data.cancellationReasonType ??
          this.internalModel.cancellation?.cancellationReasonType ??
          MaintenancePlanCancellationReasonType.OTHER,
        suppressCancellationEmail: data.suppressCancellationEmail ?? false,
        shouldExpireVisitsImmediately: data.shouldExpireVisitsImmediately ?? false,
      }
      this.internalModel.paymentSubscriptionGuid = undefined
      this.internalModel.terminatesAt = undefined
      this.internalModel.scheduledActivationAt = undefined
    }

    if (e.eventType === MaintenancePlanLapsedEventName) {
      this.internalModel.status = MaintenancePlanStatus.LAPSED
      this.internalModel.lapsedAt = e.occurredAt
      this.internalModel.paymentFlow = MaintenancePlanPaymentFlow.NONE
      this.internalModel.cancellation = undefined
    }

    if (e.eventType === MaintenancePlanExpiredEventName) {
      this.internalModel.status = MaintenancePlanStatus.EXPIRED
      this.internalModel.paymentFlow = MaintenancePlanPaymentFlow.NONE
      this.internalModel.expiredAt = e.occurredAt
      this.internalModel.lapsedAt = undefined
      this.internalModel.cancellation = undefined
    }

    if (e.eventType === MaintenancePlanLifecycleCorrectedEventName) {
      const data = e.eventData as MaintenancePlanLifecycleCorrectedEventData
      const correctionsBySource = new Map(
        data.visitCorrections.map(correction => [correction.sourceVisitGuid, correction]),
      )
      if (this.internalModel.configuration?.initialVisits) {
        this.internalModel.configuration = {
          ...this.internalModel.configuration,
          initialVisits: this.internalModel.configuration.initialVisits.map(visit => {
            const correction = correctionsBySource.get(visit.visitGuid)
            return correction
              ? {
                  ...visit,
                  visitGuid: correction.targetVisitGuid,
                  affinityDate: correction.affinityDate,
                }
              : visit
          }),
        }
      }
      this.internalModel.status = MaintenancePlanStatus.ACTIVE
      this.internalModel.paymentFlow = MaintenancePlanPaymentFlow.MANUAL
      this.internalModel.activatedAt = data.activatedAt
      this.internalModel.terminatesAt = data.terminatesAt
      this.internalModel.expiredAt = undefined
      this.internalModel.lapsedAt = undefined
      this.internalModel.cancellation = undefined
      this.internalModel.scheduledActivationAt = undefined
      this.internalModel.paymentSubscriptionGuid = undefined
    }

    if (e.eventType === MaintenancePlanPricingSetEventName) {
      const {
        subtotalPeriodPriceUsc,
        discountPeriodAmountUsc,
        paymentInterval,
        upchargePeriodAmountUsc,
        priceEngineOptions,
      } = e.eventData as MaintenancePlanPricingSetEventData
      this.internalModel.subtotalPriceUsc = subtotalPeriodPriceUsc
      this.internalModel.discountAmountUsc = discountPeriodAmountUsc
      this.internalModel.upchargeAmountUsc = upchargePeriodAmountUsc ?? 0
      this.internalModel.paymentInterval = paymentInterval
      this.internalModel.priceEngineOptions =
        priceEngineOptions ?? this.internalModel.priceEngineOptions ?? DEFAULT_PRICE_ENGINE_OPTIONS
      this.updatePricingSummary()

      this.internalModel.isPricingInitialized = true
      this.internalModel.isFreePlan = this.internalModel.totalPriceUsc === 0
    }

    if (e.eventType === MaintenancePlanPaymentReceivedEventName) {
      const { paymentRecordGuid, paymentAmountUsc, isOneTimePayment } =
        e.eventData as MaintenancePlanPaymentReceivedEventData
      this.internalModel.payments[paymentRecordGuid] = paymentAmountUsc
      this.internalModel.totalPaymentsReceivedUsc += paymentAmountUsc

      if (
        this.internalModel.status === MaintenancePlanStatus.PENDING ||
        this.internalModel.status === MaintenancePlanStatus.LAPSED
      )
        this.internalModel.status = MaintenancePlanStatus.ACTIVE

      if (this.internalModel.paymentFlow === MaintenancePlanPaymentFlow.NONE)
        this.internalModel.paymentFlow = isOneTimePayment
          ? MaintenancePlanPaymentFlow.MANUAL
          : MaintenancePlanPaymentFlow.AUTO
    }

    if (e.eventType === MaintenancePlanMigratedToStaticPricingEventName) {
      const data = e.eventData as MaintenancePlanMigratedToStaticPricingEventData
      if (this.internalModel && this.internalModel.configuration) {
        this.internalModel.configuration.yearlyStaticPriceUsc = data.yearlyStaticPriceUsc
        this.internalModel.isMigratedToStaticPricing = true
      }
    }

    if (e.eventType === MaintenancePlanSetSoldByEventName) {
      this.internalModel.soldByUserGuid = (e.eventData as MaintenancePlanSetSoldByEventData).soldByUserGuid
    }

    return
  }

  private updatePricingSummary = () => {
    if (!this.internalModel) throw notInitializedEx()
    const vm = this.internalModel
    if (!vm.paymentInterval) return

    const periodCartSummary = calculateSimplePriceOrderSummaryUsc(
      vm.subtotalPriceUsc,
      vm.discountAmountUsc,
      vm.upchargeAmountUsc,
      vm.taxRate?.rate ?? 0,
      vm.priceEngineOptions,
    )

    this.internalModel = {
      ...vm,
      ...periodCartSummary,
      // NOTE: Not quite right. This might not be a yearly price anymore
      yearlyPriceUsc: Math.trunc(periodCartSummary.totalPriceUsc * NumPeriodsPerYearForInterval[vm.paymentInterval]),
    }
  }

  private requireNotInitialized = (): void => {
    if (this.isInitialized()) {
      throw new BusinessResourceConflictException('Maintenance Plan is already initialized')
    }
  }

  private requireInitialized = (): void => {
    if (!this.isInitialized()) {
      throw notInitializedEx()
    }
  }

  private pushForUser = async <TEventData>(eventType: string, eventDataForUser: ForUser<TEventData>): Promise<void> =>
    await this.push({ eventType, eventData: withoutUserGuid(eventDataForUser), userGuid: eventDataForUser.userGuid })

  private push = async <TEventData>({
    eventType,
    eventData,
    userGuid,
  }: ForUser<EventPayload<TEventData>>): Promise<void> => {
    this.requireInitialized()
    return this.persistAndApplyEvent(this.createNextEvent({ eventType, eventData, userGuid }))
  }

  private persistAndApplyEvent = async (e: Event<unknown>): Promise<void> => {
    await this.eventStore.create(e)
    this.applyEvent(e)
  }

  private createNextEvent = <TEventData>({
    eventType,
    eventData,
    userGuid,
  }: ForUser<EventPayload<TEventData>>): Event<TEventData> => {
    const { maintenancePlanGuid, companyGuid, maintenancePlanVersion } = this.getComprehensiveViewModel()

    return {
      companyGuid,
      entityType: MaintenancePlanEntityTypeName,
      entityGuid: maintenancePlanGuid,
      entityVersion: maintenancePlanVersion + 1,
      actingUserGuid: userGuid,
      occurredAt: this.clock(),
      eventType,
      eventData,
    }
  }
}

const vmSetActivatedAt = (
  vm: Mutable<BasicMaintenancePlanViewModel>,
  activatedAt: IsoDateString,
  numDaysUntilAutoCancelation?: number,
) => {
  // NOTE: In case of a secondary activation, for example due to migration, original activation date is kept
  if (!vm.activatedAt || vm.status !== MaintenancePlanStatus.ACTIVE) {
    vm.activatedAt = activatedAt
  }
  vm.status = MaintenancePlanStatus.ACTIVE
  vm.lapsedAt = undefined
  vm.cancellation = undefined
  vm.terminatesAt = vm.configuration?.numFixedDurationYears
    ? vmCalcFixedDurationTerminatesAt(activatedAt, vm.configuration.numFixedDurationYears)
    : vmCalcTerminatesAt(activatedAt, numDaysUntilAutoCancelation)
}

const vmCalcFixedDurationTerminatesAt = (activatedAt: IsoDateString, numYears: number): IsoDateString =>
  BzDateFns.withUtc(activatedAt, d => BzDateFns.addYears(d, numYears))

const vmCalcTerminatesAt = (
  activatedAt: IsoDateString,
  numDaysUntilAutoCancelation?: number,
): IsoDateString | undefined =>
  numDaysUntilAutoCancelation
    ? BzDateFns.withUtc(activatedAt, d => BzDateFns.addDays(d, numDaysUntilAutoCancelation))
    : undefined

export const SendMaintenancePlanVisitReminderDtoSchema = z.object({
  sendConfirmationEnabled: bzOptional(z.boolean()),
  notificationType: bzOptional(z.enum(['SMS', 'EMAIL'])),
  to: bzOptional(z.string()),
  sendReminderEnabled: bzOptional(z.boolean()),
})

export type MaintenancePlanVisitGuid = Guid
export type MaintenancePlanVisitReminderDTO = z.infer<typeof SendMaintenancePlanVisitReminderDtoSchema>
export type MaintenancePlanVisitReminderSenderRequest = {
  maintenancePlanVisitGuid: MaintenancePlanVisitGuid
  arrivalWindow: TimeWindow
} & MaintenancePlanVisitReminderDTO

export type MaintenancePlanVisitReminderSender = AsyncFn<ForCompany<MaintenancePlanVisitReminderSenderRequest>>
